import { create } from 'zustand';

const MAX_ISSUED_SYNC_KEYS = 200;

interface AnalyticsAutoSyncMemoryState {
  issuedSyncKeys: ReadonlySet<string>;
  dismissedAutoSyncKeys: ReadonlySet<string>;
  markIssuedSyncForSelectionKey: (selectionKey: string) => void;
  hasIssuedSyncForSelectionKey: (selectionKey: string) => boolean;
  markDismissedAutoSyncForSelectionKey: (selectionKey: string) => void;
  hasDismissedAutoSyncForSelectionKey: (selectionKey: string) => boolean;
}

/**
 * In-memory guard preventing a second auto-sync for the same selection within a
 * session. Whether a profile has *ever* been synced is authoritative on the
 * server (`PageHealth.last_successful_sync_at`) and is what gates the initial
 * auto-sync, so this store deliberately does not persist across reloads.
 */
export const useAnalyticsAutoSyncMemory = create<AnalyticsAutoSyncMemoryState>(
  (set, get) => ({
    issuedSyncKeys: new Set(),
    dismissedAutoSyncKeys: new Set(),
    markIssuedSyncForSelectionKey: (selectionKey) =>
      set((s) => {
        const ordered = Array.from(s.issuedSyncKeys);
        if (!ordered.includes(selectionKey)) {
          ordered.push(selectionKey);
        }
        const trimmed =
          ordered.length > MAX_ISSUED_SYNC_KEYS
            ? ordered.slice(-MAX_ISSUED_SYNC_KEYS)
            : ordered;
        return { issuedSyncKeys: new Set(trimmed) };
      }),
    hasIssuedSyncForSelectionKey: (selectionKey) =>
      get().issuedSyncKeys.has(selectionKey),
    markDismissedAutoSyncForSelectionKey: (selectionKey) =>
      set((s) => {
        const ordered = Array.from(s.dismissedAutoSyncKeys);
        if (!ordered.includes(selectionKey)) {
          ordered.push(selectionKey);
        }
        const trimmed =
          ordered.length > MAX_ISSUED_SYNC_KEYS
            ? ordered.slice(-MAX_ISSUED_SYNC_KEYS)
            : ordered;
        return { dismissedAutoSyncKeys: new Set(trimmed) };
      }),
    hasDismissedAutoSyncForSelectionKey: (selectionKey) =>
      get().dismissedAutoSyncKeys.has(selectionKey),
  }),
);
